import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertOctagon, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

// Lands here after Paystack redirects back. We MUST verify server-side
// before trusting anything in the URL — the user could tamper with the
// reference param. The backend re-fetches the transaction from Paystack.
export default function SubscriptionCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | failed
  const [message, setMessage] = useState('');
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const ref = params.get('reference') || params.get('trxref') || sessionStorage.getItem('pharmxai_pending_ref');
    if (!ref) {
      setStatus('failed');
      setMessage('Missing payment reference — please retry from the Subscription page.');
      return;
    }
    api.post('/billing/verify', { reference: ref })
      .then(({ data }) => {
        if (data.success) {
          setStatus('success');
          setSubscription(data.subscription);
          sessionStorage.removeItem('pharmxai_pending_ref');
        } else {
          setStatus('failed');
          setMessage(data.message || 'Payment was not successful');
        }
      })
      .catch((e) => {
        setStatus('failed');
        setMessage(e?.response?.data?.detail || 'Could not verify payment with the server.');
      });
  }, [params]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="subscription-callback-page">
      <div className="max-w-md w-full">
        {status === 'verifying' && (
          <div className="rounded-2xl border bg-card p-10 text-center" data-testid="callback-verifying">
            <Loader2 className="h-12 w-12 text-emerald-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold mb-2">Verifying payment…</h2>
            <p className="text-sm text-muted-foreground">
              Hold on — we're confirming your payment with Paystack.
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="rounded-2xl border border-emerald-300 bg-emerald-50/40 p-10 text-center" data-testid="callback-success">
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Payment successful 🎉</h2>
            <p className="text-sm text-muted-foreground mb-6">
              You're now on the <span className="font-semibold capitalize">{subscription?.tier}</span> plan
              ({subscription?.cycle} billing). All features have been unlocked.
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => navigate('/subscription')} variant="outline" data-testid="callback-go-sub-btn">
                View subscription
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="callback-go-dash-btn"
              >
                Go to Dashboard <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="rounded-2xl border border-rose-300 bg-rose-50/40 p-10 text-center" data-testid="callback-failed">
            <AlertOctagon className="h-16 w-16 text-rose-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Payment failed</h2>
            <p className="text-sm text-muted-foreground mb-6">{message}</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => navigate('/subscription')} className="bg-rose-600 hover:bg-rose-700" data-testid="callback-retry-btn">
                Try again
              </Button>
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
